import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";

// Player Portal registrations only auto-link to the roster when the typed
// name matches exactly (see routes/player.js /register) — a spelling
// difference (Derek vs Derick, a missing middle name, etc.) leaves a real
// login sitting unconnected to that person's actual history. This screen
// shows the full picture — who's registered and linked, and who's
// registered but needs manual linking — instead of it needing a
// hand-edited database fix every time.
export default function LinkAccounts() {
  const [linked, setLinked] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [notRegistered, setNotRegistered] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [toast, setToast] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/players/portal-status"), api.get("/players")])
      .then(([statusRes, playerRes]) => {
        setLinked(statusRes.data.linked);
        setUnlinked(statusRes.data.unlinked);
        setNotRegistered(statusRes.data.notRegistered || []);
        setPlayers(playerRes.data.filter(p => p.active));
      })
      .catch(() => setToast("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const linkAccount = async (accountId) => {
    const playerId = selected[accountId];
    if (!playerId) { showToast("Choose a player first"); return; }
    try {
      await api.put(`/players/link-account/${accountId}`, { player_id: parseInt(playerId) });
      showToast("Linked!");
      load();
    } catch(e) {
      showToast("Failed: " + (e.response?.data?.error || e.message));
    }
  };

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-3">
          <span className="font-semibold text-sm">Register Player Accounts</span>
          <span className="ml-3 text-xs text-gray-500">Who's registered for the Player Portal, and whether they're linked to your roster</span>
        </div>
        {toast && <div className="bg-green-50 text-green-700 text-sm px-4 py-2 border-b border-green-200">{toast}</div>}
        {loading && <div className="p-4 text-gray-500 text-sm">Loading...</div>}
        {!loading && (
          <div className="flex-1 overflow-auto p-4 flex flex-col gap-4">

            {/* Primary view: who's registered vs who isn't -- the question
                organisers actually come to this page to answer. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">

              <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-green-800 text-white text-xs font-bold px-4 py-2">REGISTERED ({linked.length})</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="px-4 py-1.5 font-medium">Name</th>
                      <th className="px-4 py-1.5 font-medium">Email</th>
                      <th className="px-4 py-1.5 font-medium">Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linked.map(a => (
                      <tr key={a.id} className="border-b border-gray-100 last:border-0">
                        <td className="px-4 py-2 font-medium">{a.player_name}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{a.email}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</td>
                      </tr>
                    ))}
                    {linked.length === 0 && <tr><td colSpan={3} className="px-4 py-3 text-gray-400 text-sm">No one has registered yet.</td></tr>}
                  </tbody>
                </table>
              </div>

              {/* On the roster, no Player Portal account at all yet. */}
              <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-500 text-white text-xs font-bold px-4 py-2">NOT REGISTERED ({notRegistered.length})</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="px-4 py-1.5 font-medium">Name</th>
                      <th className="px-4 py-1.5 font-medium">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notRegistered.map(p => (
                      <tr key={p.id} className="border-b border-gray-100 last:border-0">
                        <td className="px-4 py-2 font-medium">{p.name}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{p.email}</td>
                      </tr>
                    ))}
                    {notRegistered.length === 0 && <tr><td colSpan={2} className="px-4 py-3 text-gray-400 text-sm">Everyone on the roster has registered.</td></tr>}
                  </tbody>
                </table>
              </div>

            </div>

            {/* Secondary: name-mismatch cleanup. Demoted below the main view
                since it's usually empty -- most registrations auto-link. */}
            <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-amber-600 text-white text-xs font-bold px-4 py-2">NEEDS LINKING ({unlinked.length}) — registered, but the name didn't match the roster exactly</div>
              <table className="w-full text-sm">
                <tbody>
                  {unlinked.map(a => (
                    <tr key={a.id} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2">
                        <div className="font-medium">{a.first_name} {a.last_name}</div>
                        <div className="text-xs text-gray-500">{a.email}</div>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          className="border border-gray-300 rounded px-2 py-1 text-xs"
                          value={selected[a.id] || ""}
                          onChange={e => setSelected(prev => ({ ...prev, [a.id]: e.target.value }))}
                        >
                          <option value="">— Select player —</option>
                          {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => linkAccount(a.id)}
                          className="bg-green-700 hover:bg-green-800 text-white text-xs font-semibold px-3 py-1.5 rounded"
                        >
                          Link
                        </button>
                      </td>
                    </tr>
                  ))}
                  {unlinked.length === 0 && <tr><td className="px-4 py-3 text-gray-400 text-sm">Nothing to link.</td></tr>}
                </tbody>
              </table>
            </div>

          </div>
        )}
      </div>
    </Layout>
  );
}
